/**
 * Releases Index Page
 *
 * @module features/releases/pages/ReleasesIndex
 * @description Index page shown when no property is selected.
 * Displays a property picker or empty state if no configs exist.
 *
 * @see docs/api/services/release-qa.md
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { House, Gear, CircleNotch } from '@phosphor-icons/react';
import { Button, Card, Text, Heading } from '@chatsdet/ui';
import { buildHeadLabel } from '../buildLabels';
import { useReleaseProperties } from '../hooks/useReleaseProperties';

export function ReleasesIndex() {
  const navigate = useNavigate();
  const { data: properties, isLoading, error } = useReleaseProperties();

  // Auto-redirect to first property if only one exists
  React.useEffect(() => {
    if (properties && properties.length === 1) {
      navigate({ to: '/releases/$propertyId', params: { propertyId: properties[0]!.propertyId } });
    }
  }, [properties, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <CircleNotch className="animate-spin text-neutral-400" size={32} />
      </div>
    );
  }

  // Error state - feature may not be enabled
  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="max-w-md p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-red-100 p-4">
              <Gear size={32} className="text-red-500" />
            </div>
          </div>
          <Heading level={3} className="mb-2">
            Unable to Load Releases
          </Heading>
          <Text className="text-neutral-500 mb-2">
            {error.message?.includes('403') || error.message?.includes('not enabled')
              ? 'Release QA is not enabled for your organization. Contact support to enable this feature.'
              : 'Failed to load release applications. Please try again.'}
          </Text>
          <Text size="sm" className="text-neutral-400">
            {error.message}
          </Text>
        </Card>
      </div>
    );
  }

  // No configs - show setup prompt
  if (!properties || properties.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="max-w-md p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-neutral-100 p-4">
              <Gear size={32} className="text-neutral-500" />
            </div>
          </div>
          <Heading level={3} className="mb-2">
            No Release QA Configured
          </Heading>
          <Text className="text-neutral-500 mb-6">
            Configure Release QA on an application to start tracking commits and running automated release testing.
          </Text>
          <Button onPress={() => navigate({ to: '/applications' })}>
            Configure Applications
          </Button>
        </Card>
      </div>
    );
  }

  // Multiple properties - show picker
  return (
    <div className="p-8">
      <Heading level={2} className="mb-6">
        Select an Application
      </Heading>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {properties.map((property) => (
          <Card
            key={property.propertyId}
            className="p-4 cursor-pointer hover:border-primary-500 transition-colors"
            onClick={() =>
              navigate({ to: '/releases/$propertyId', params: { propertyId: property.propertyId } })
            }
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-neutral-100 p-2">
                <House size={20} className="text-neutral-600" />
              </div>
              <div className="flex-1 min-w-0">
                <Text weight="semibold" className="truncate">
                  {property.propertyName}
                </Text>
                {property.mode === 'build' ? (
                  <Text size="sm" className="text-muted-foreground">
                    {buildHeadLabel(property.head)}
                  </Text>
                ) : (
                  <>
                    {property.releaseBranch && (
                      <Text size="sm" className="text-neutral-500">
                        Branch: {property.releaseBranch}
                      </Text>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      {property.untestedCommits > 0 && (
                        <Text size="sm" className="text-amber-600">
                          {property.untestedCommits} untested commit{property.untestedCommits !== 1 ? 's' : ''}
                        </Text>
                      )}
                      {property.lastRunStatus && (
                        <Text size="sm" className="text-neutral-500">
                          Last run: {property.lastRunStatus}
                        </Text>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
